import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, Target, Keyboard, Award, AlertTriangle, ArrowRight, Mic } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, Legend,
} from "recharts";

function Stat({ icon: Icon, label, value, suffix, testid }) {
  return (
    <div className="p-5 rounded-md border border-border bg-card hover:-translate-y-1 transition-transform" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <div className="font-mono text-3xl font-bold">
        {value}
        {suffix && <span className="text-lg text-muted-foreground ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  if (isLoading)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const d = data || {};
  const axisStyle = { fontSize: 11, fontFamily: "JetBrains Mono", fill: "hsl(var(--muted-foreground))" };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Welcome back</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">{user?.name}</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Target} label="Avg Score" value={d.avg_score ?? 0} suffix="%" testid="stat-avg-score" />
        <Stat icon={Award} label="Tests Taken" value={d.tests_taken ?? 0} testid="stat-tests-taken" />
        <Stat icon={Keyboard} label="Best WPM" value={d.best_wpm ?? 0} testid="stat-best-wpm" />
        <Stat icon={TrendingUp} label="Avg WPM" value={d.avg_wpm ?? 0} testid="stat-avg-wpm" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-6 rounded-md border border-border bg-card">
          <h3 className="font-heading font-bold text-lg mb-4">Score History</h3>
          {d.score_history?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={d.score_history}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis domain={[0, 100]} tick={axisStyle} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }} />
                <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Take an assessment to see your scores here." to="/quizzes" cta="Start assessment" />
          )}
        </div>

        <div className="p-6 rounded-md border border-border bg-card">
          <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-primary" /> Weak Areas
          </h3>
          {d.weak_areas?.length ? (
            <div className="space-y-3" data-testid="weak-areas">
              {d.weak_areas.map((w) => (
                <div key={w.category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{w.category}</span>
                    <span className="font-mono text-muted-foreground">{w.avg}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className={`h-full rounded-full ${w.avg < 50 ? "bg-destructive" : w.avg < 75 ? "bg-chart-4" : "bg-success"}`} style={{ width: `${w.avg}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No data yet. Complete assessments to identify weak areas.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-6 rounded-md border border-border bg-card">
          <h3 className="font-heading font-bold text-lg mb-4">Typing Speed History</h3>
          {d.typing_history?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.typing_history}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }} />
                <Bar dataKey="wpm" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Try a typing test to track your WPM." to="/typing" cta="Start typing test" />
          )}
        </div>

        <div className="p-6 rounded-md border border-border bg-card">
          <h3 className="font-heading font-bold text-lg mb-4">Recent Activity</h3>
          {d.recent_submissions?.length ? (
            <div className="space-y-2" data-testid="recent-activity">
              {d.recent_submissions.map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.quiz_title}</p>
                    <p className="text-xs text-muted-foreground font-mono">{s.category}</p>
                  </div>
                  <span className={`font-mono font-bold text-sm ${s.score >= 75 ? "text-success" : s.score >= 50 ? "text-chart-4" : "text-destructive"}`}>{s.score}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No recent activity.</p>
          )}
        </div>
      </div>

      <div className="p-6 rounded-md border border-border bg-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Mic className="w-4 h-4 text-primary" /> Speaking Trends</h3>
          {d.speaking_tests > 0 && (
            <span className="text-xs font-mono text-muted-foreground">Avg overall <span className="text-foreground font-bold">{d.speaking_avg}</span> · {d.speaking_tests} sessions</span>
          )}
        </div>
        {d.speaking_history?.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={d.speaking_history}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={axisStyle} />
              <YAxis domain={[0, 100]} tick={axisStyle} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11 }} />
              <Line type="monotone" dataKey="pronunciation" name="Pronunciation" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="fluency" name="Fluency" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="overall" name="Overall" stroke="hsl(var(--chart-5))" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart label="Practice Voice & Accent to track pronunciation and fluency over time." to="/versant" cta="Start speaking practice" />
        )}
      </div>
    </div>
  );
}

function EmptyChart({ label, to, cta }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <p className="text-sm text-muted-foreground mb-4">{label}</p>
      <Link to={to} className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline">
        {cta} <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}
